import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Clock,
} from 'lucide-react'
// Unused: Download, Eye, Trash2, MoreHorizontal, ArrowUpDown, ChevronDown
import { cn } from '@/lib/utils'
// Unused imports:
// import { Card, CardContent } from '@/components/ui/card'
// import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
// import { Button, Checkbox, Input, Badge } from '@/components/ui/...'
// import { formatDate } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { InvoiceReviewSection } from './invoice-sections/InvoiceReviewSection'
import { InvoiceBatchExportSection } from './invoice-sections/InvoiceBatchExportSection'
import { apiClient } from '@/lib/api'
// import { exportInvoice } from '@/services/invoiceService'

interface Client {
  id: string
  name: string
  status: string
}



interface Invoice {
  id: string
  original_filename: string
  file_size: number
  mime_type: string
  status: 'uploaded' | 'processing' | 'completed' | 'failed' | 'review_required' | 'reviewed' | 'exported'
  overall_confidence?: number
  uploaded_at: string
  processed_at?: string
  error_message?: string
  client?: Client
}

interface ReviewContext {
  type: 'selected' | 'all_pending'
  invoiceIds: string[]
}

interface InvoicesDashboardProps {
  onInvoiceSelect?: (invoiceId: string) => void
  onInvoiceReview?: (invoiceId: string, context?: ReviewContext) => void
}

export default function InvoiceProcess() {
  const steps = [
    {
      number: "01",
      title: "Upload Documents",
      desc: (
        <>
          <strong>Upload your invoices.</strong> Note that some invoices are
          automatically received by email or watched folders. Our system
          immediately processes all invoices and prepares them for review.
        </>
      ),
    },
    {
      number: "02",
      title: "Review",
      desc: (
        <>
          <strong>Review the already processed invoices.</strong> Watch for
          mistakes and inconsistencies. Our AI is pretty good when processing
          most documents but makes mistakes in certain cases.
        </>
      ),
    },
    {
      number: "03",
      title: "Export to .XML",
      desc: (
        <>
          <strong>Export reviewed documents</strong> to an .XML file and upload
          it to accounting software. This way you update the accounting software database with the latest
          changes.
        </>
      ),
    },
    {
      number: "04",
      title: "Import Bank Statement",
      desc: (
        <>
          <strong>Upload bank statement.</strong> Go to clients' bank and
          download the statement then come here, select client and upload the
          file. Our system automatically pairs transactions to invoices.
        </>
      ),
    },
    {
      number: "05",
      title: "Match",
      desc: (
        <>
          <strong>Match and review all transaction pairs.</strong> Check for
          mistakes and match what the AI could figure out.
        </>
      ),
    },
    {
      number: "06",
      title: "Export to .XML",
      desc: (
        <>
          <strong>Export matched pairs</strong> to a .XML file and upload it to
          accounting software. This way everything is up to date and the process is complete.
        </>
      ),
    },
  ];

  return (
    <section className="w-full bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-6">The Invoice Process</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
        {steps.map((step, i) => (
          <div key={i} className="flex items-start space-x-3">
            <span className="flex items-center justify-center px-3 py-1 rounded-full bg-gray-200 text-gray-700 font-semibold text-xs font-mono">
              {step.number}
            </span>
            <div>
              <h3 className="font-semibold">{step.title}</h3>
              <p className="text-gray-600 text-sm mt-1">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Unused code removed (status badges array)

export function InvoicesDashboard({
  onInvoiceSelect: _onInvoiceSelect,
  onInvoiceReview
}: InvoicesDashboardProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [pendingReviewInvoices, setPendingReviewInvoices] = useState<Invoice[]>([])
  const [pendingReviewTotal, setPendingReviewTotal] = useState<number>(0)
  const pendingReviewCacheRef = useRef<{ invoices: Invoice[]; total: number } | null>(null)

  // const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const fetchPendingReviewData = useCallback(async (forceRefresh = false): Promise<Invoice[]> => {
    if (!forceRefresh && pendingReviewCacheRef.current) {
      const { invoices: cachedInvoices, total: cachedTotal } = pendingReviewCacheRef.current
      setPendingReviewInvoices(cachedInvoices)
      setPendingReviewTotal(cachedTotal)
      return cachedInvoices
    }

    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        status: 'review_required'
      })

      const response = await apiClient.fetch(`/invoices/?${params}`)

      if (response.ok) {
        const data = await response.json()
        const invoicesForReview: Invoice[] = data.invoices || []
        const total = typeof data.total === 'number'
          ? data.total
          : invoicesForReview.length

        pendingReviewCacheRef.current = { invoices: invoicesForReview, total }
        setPendingReviewInvoices(invoicesForReview)
        setPendingReviewTotal(total)
        localStorage.setItem('pendingReviewCount', total.toString())

        return invoicesForReview
      }
    } catch (error) {
      console.error('Error fetching pending review invoices:', error)
    }

    if (pendingReviewCacheRef.current) {
      const { invoices: cachedInvoices, total: cachedTotal } = pendingReviewCacheRef.current
      setPendingReviewInvoices(cachedInvoices)
      setPendingReviewTotal(cachedTotal)
      return cachedInvoices
    }

    return []
  }, [])

  const loadInvoices = useCallback(async () => {
    try {
      const response = await apiClient.fetch('/invoices/')

      if (response.ok) {
        const data = await response.json()
        setInvoices(data.invoices || [])
        await fetchPendingReviewData()
      }
    } catch (error) {
      console.error('Error loading invoices:', error)
    } finally {
      setLoading(false)
    }
  }, [fetchPendingReviewData])

  useEffect(() => {
    void loadInvoices()
  }, [loadInvoices])

  useEffect(() => {
    try {
      const storedCount = localStorage.getItem('pendingReviewCount')
      if (storedCount) {
        const parsed = Number(storedCount)
        if (!Number.isNaN(parsed)) {
          setPendingReviewTotal(parsed)
        }
      }
    } catch (error) {
      console.error('Error restoring pending review count:', error)
      localStorage.removeItem('pendingReviewCount')
    }
  }, [])

  // Check for navigation state to trigger review all pending
  const handleReviewAllPending = useCallback(async () => {
    let pendingInvoices = invoices.filter(inv => inv.status === 'review_required')

    if (pendingInvoices.length === 0) {
      pendingInvoices = pendingReviewInvoices
    }

    if (pendingInvoices.length === 0) {
      pendingInvoices = await fetchPendingReviewData()
    }

    if (pendingInvoices.length > 0 && onInvoiceReview) {
      const pendingIds = pendingInvoices.map(inv => inv.id)
      localStorage.removeItem('selectedInvoices')
      onInvoiceReview(pendingInvoices[0].id, { type: 'all_pending', invoiceIds: pendingIds })
      return
    }

    if (pendingInvoices.length > 0) {
      localStorage.removeItem('selectedInvoices')
      localStorage.setItem('pendingReviewCount', pendingInvoices.length.toString())
      navigate('/invoices/processing', {
        state: {
          triggerReviewAll: true
        }
      })
    } else {
      navigate('/invoices/processing')
    }
  }, [invoices, pendingReviewInvoices, fetchPendingReviewData, onInvoiceReview, navigate])

  useEffect(() => {
    const state = location.state as { triggerReviewAll?: boolean } | null
    if (!state?.triggerReviewAll) {
      return
    }

    const executeReviewAll = async () => {
      await handleReviewAllPending()
    }

    void executeReviewAll()
  }, [location.state, handleReviewAllPending])
  // Unused functions (kept for future use)
  // const deleteInvoice = async (invoiceId: string) => { ... }
  // const downloadInvoice = async (invoiceId: string) => { ... }

  const handleBatchExport = async () => {
    const exportableInvoices = invoices.filter(inv => inv.status === 'reviewed' || inv.status === 'exported')
    if (exportableInvoices.length === 0) {
      alert('No reviewed invoices available for export.')
      return
    }

    setIsExporting(true)
    try {
      const response = await apiClient.fetch('/invoices/batch-export', {
        method: 'POST',
        body: JSON.stringify({
          invoice_ids: exportableInvoices.map(inv => inv.id)
        })
      })

      if (response.ok) {
        const data = await response.json()

        // Download the file programmatically with authentication
        try {
          const downloadPath = data.download_url.startsWith('/api/v1')
            ? data.download_url.substring('/api/v1'.length)
            : data.download_url

          const downloadResponse = await apiClient.fetch(downloadPath)

          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = data.filename || 'batch_export.zip'
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
          } else {
            alert('Download failed: Unable to retrieve the export file.')
          }
        } catch (downloadError) {
          console.error('Error downloading file:', downloadError)
          alert('Download failed: Network error occurred.')
        }
      } else {
        const errorText = await response.text()
        console.error('Batch export failed:', response.status, errorText)
        alert(`Batch export failed: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error in batch export:', error)
      alert(`Error during batch export: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsExporting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-spin" />
          <p className="text-muted-foreground">Loading invoices...</p>
        </div>
      </div>
    )
  }

  const pendingReviewCount = pendingReviewTotal || invoices.filter(inv => inv.status === 'review_required').length
  const exportableCount = invoices.filter(inv => inv.status === 'reviewed' || inv.status === 'exported').length

  return (
    <div className="page-shell space-y-6">
      <div className={cn("flex flex-col w-full items-start gap-6 relative bg-white py-6")}>
        {/* Header */}
        <section className="flex flex-col items-start gap-4 relative flex-1 self-stretch w-full grow">
          <header className="flex items-center gap-24 relative self-stretch w-full flex-[0_0_auto] translate-y-[-1rem]">
            <div className="inline-flex flex-col items-start justify-center gap-1.5 relative flex-[0_0_auto]">
              <div className="inline-flex items-center justify-center gap-2.5 relative flex-[0_0_auto]">
                <h1 className=" text-neutral-950 text-[length:var(--text-2xl-semi-bold-font-size)] leading-[var(--text-2xl-semi-bold-line-height)] relative w-fit font-text-2xl-semi-bold font-[number:var(--text-2xl-semi-bold-font-weight)] tracking-[var(--text-2xl-semi-bold-letter-spacing)] whitespace-nowrap [font-style:var(--text-2xl-semi-bold-font-style)]">
                  Invoices
                </h1>
              </div>
            </div>
          </header>
        </section>

        {/* Upload, Review, and Batch Export Section */}
        <div className="flex flex-col items-end gap-6 px-6 py-6 relative self-stretch w-full flex-[0_0_auto] bg-neutral-100 rounded-xl translate-y-[-1rem] [--animation-delay:200ms]">
          <div className="flex items-center gap-4 py-0 relative self-stretch w-full flex-[0_0_auto]">
            <Card className="relative flex min-h-[280px] flex-1 grow items-start justify-end gap-6 rounded-xl border border-neutral-200 bg-white px-0 py-6 shadow-sm">
              <CardContent className="flex flex-col items-start gap-6 px-6 py-0 relative flex-1 self-stretch grow">
                <div className="flex flex-col items-start gap-6 relative flex-1 grow self-stretch">
                  <div className="relative flex w-full flex-[0_0_auto] flex-col items-start justify-center gap-1.5 self-stretch">
                    <div className="relative flex w-full flex-[0_0_auto] items-center justify-between self-stretch">
                      <span className="whitespace-nowrap text-base font-semibold text-neutral-950">
                        Upload
                      </span>
                    </div>
                    <div className="relative flex w-full flex-[0_0_auto] gap-2.5 self-stretch">
                      <p className="flex-1 text-sm font-normal leading-6 text-neutral-500">
                        Upload your invoices for processing
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex w-full items-end gap-2 relative flex-[0_0_auto]">
                  <Button
                    onClick={() => navigate('/invoices/processing')}
                    className="h-9 rounded-lg bg-[#0000ff] px-4 py-2 text-sm font-medium leading-5 text-neutral-50 shadow-box-shadow-shadow-xs hover:bg-[#3333ff]"
                  >
                    Upload Invoices
                  </Button>
                </div>
              </CardContent>
            </Card>

            <InvoiceReviewSection
              pendingReviewCount={pendingReviewCount}
              onReviewAllPending={handleReviewAllPending}
              selectedInvoicesCount={0}
            />

            <InvoiceBatchExportSection
              exportableCount={exportableCount}
              onBatchExport={handleBatchExport}
              isExporting={isExporting}
            />
          </div>

          {/* Invoice Process */}
        </div>
      </div>
      <InvoiceProcess />
    </div>
  )
}
